import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CheckCircle2,
  Download,
  FileSpreadsheet,
  MinusCircle,
  Upload,
  XCircle,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadCsv, downloadPrivateFile } from '@/lib/download';
import { cn } from '@/lib/utils';
import type { ImportResult, ImportRowResult } from '@/types/institute';
import { ImportRejectionList } from './ImportRejectionList';
import { SharedMobilesCheck } from './SharedMobilesCheck';
import { STUDENT_ACCOUNT_LABEL } from '../constants';
import { admissionsApi, type ImportSheetOptions } from '../api/admissions.api';

type OutcomeTab = 'all' | 'ADMITTED' | 'REJECTED' | 'SKIPPED';

/**
 * Admitting many students from one spreadsheet.
 *
 * An office that has just enrolled two hundred children already has the list in
 * Excel, and will not retype it into a form two hundred times — so the product
 * meets it where the data already is.
 *
 * The panel is deliberately ordered the way the job is done: get the template,
 * fill it in, upload it, read what happened. The results table is the part that
 * matters — it reports every problem against the **spreadsheet's own row
 * number**, so the office fixes four cells and uploads the same file again
 * rather than hunting for what broke the batch.
 *
 * Re-uploading is safe by design: the server recognises anybody already on the
 * roll and skips them, which is exactly what happens when somebody corrects
 * row 12 and sends the whole sheet back.
 */

const STATUS: Record<
  ImportRowResult['status'],
  {
    tone: 'success' | 'warning' | 'critical';
    icon: typeof CheckCircle2;
    row: string;
  }
> = {
  ADMITTED: { tone: 'success', icon: CheckCircle2, row: 'bg-ok-bg/40' },
  SKIPPED: { tone: 'warning', icon: MinusCircle, row: 'bg-warn-bg/30' },
  REJECTED: { tone: 'critical', icon: XCircle, row: 'bg-crit-bg/30' },
};

/**
 * What one import is *of* — where its template and upload go, and the words it
 * uses for the people in it.
 *
 * The steps, the results table and the rejection list are the same job for
 * students and for staff, so they are written once and told what they are
 * importing, rather than copied for a second register (CLAUDE.md §3). Each
 * importer adapts its server's result to `ImportResult` before it gets here.
 */
export interface BulkImportConfig {
  templatePath: string;
  templateFileName: string;
  upload: (sheet: File, options?: ImportSheetOptions) => Promise<ImportResult>;
  /** Who a row is — "student" / "staff member". */
  noun: { one: string; many: string };
  /** What happened to a row that went in — "admitted" / "added". */
  done: string;
  /** Why a row was skipped, finishing "… already on the roll". */
  alreadyThere: string;
  /** The "fill it in" instructions: the rules most likely to trip somebody up. */
  fillInHint: ReactNode;
  /** The upload step's note on what makes a re-upload safe. */
  uploadHint: string;
}

/** The default: students, which is what every caller but the staff room imports. */
export const STUDENT_IMPORT: BulkImportConfig = {
  templatePath: admissionsApi.templatePath(),
  templateFileName: 'student-import-template.xlsx',
  upload: admissionsApi.importSheet,
  noun: { one: 'student', many: 'students' },
  done: 'admitted',
  alreadyThere: 'already on the roll',
  fillInHint: (
    <>
      Delete the example row. Fill in every column the <strong>Notes</strong> sheet marks as
      required — the same details the admission form asks for. Write dates as{' '}
      <strong>YYYY-MM-DD</strong>.
    </>
  ),
  uploadHint:
    'Excel (.xlsx) or CSV. Anybody already on the roll is skipped, so uploading a corrected sheet again is safe.',
};

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function statusLabel(status: ImportRowResult['status'], config: BulkImportConfig): string {
  if (status === 'ADMITTED') return capitalise(config.done);
  return status === 'SKIPPED' ? 'Skipped' : 'Needs fixing';
}

export interface BulkImportPanelProps {
  /**
   * Fires after an import that admitted somebody, carrying what happened.
   *
   * The outcome is handed over rather than kept here, because what to *do*
   * about it — refresh a list, leave the page — is the caller's decision, and
   * a panel that navigated on its own could not be reused anywhere else.
   */
  onImported?: (result: ImportResult) => void;
  /** Notifies parent component whenever the import result changes. */
  onResultChange?: (result: ImportResult | null) => void;
  /** What is being imported. Students unless the caller says otherwise. */
  config?: BulkImportConfig;
}

/**
 * The second line of the toast — what happened to the rest of the sheet.
 *
 * A partial import is not a success with a footnote. If anybody was left out,
 * the count says so first and by name in the panel below, because "183
 * students admitted" is what the office will remember and act on.
 */
function describeOutcome(result: ImportResult, config: BulkImportConfig): string {
  const parts: string[] = [];

  if (result.rejected > 0) {
    parts.push(
      `${result.rejected} ${result.rejected === 1 ? `${config.noun.one} was` : `${config.noun.many} were`} NOT ${config.done}`,
    );
  }
  if (result.skipped > 0) {
    parts.push(
      `${result.skipped} ${result.skipped === 1 ? 'was' : 'were'} ${config.alreadyThere}`,
    );
  }

  if (parts.length === 0) return 'Every row went in.';

  return `${parts.join(' and ')} — see the list below.`;
}

/**
 * The import itself: get the template, fill it in, upload it, read what
 * happened.
 *
 * Split out from the card below so the same three steps can be shown inside a
 * dialog on the student and staff registers, where a permanently-expanded panel
 * would push the register itself off the screen. All the behaviour lives here;
 * the card is only chrome (CLAUDE.md §3).
 */
export function BulkImportSteps({ onImported, onResultChange, config = STUDENT_IMPORT }: BulkImportPanelProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  /**
   * The sheet held back while the office checks shared parent mobiles. Kept so
   * confirming sends this same file again rather than asking for it twice.
   */
  const [heldSheet, setHeldSheet] = useState<{ file: File; result: ImportResult } | null>(null);
  const [activeTab, setActiveTab] = useState<OutcomeTab>('all');

  useEffect(() => {
    return () => {
      onResultChange?.(null);
    };
  }, [onResultChange]);

  async function downloadTemplate() {
    setIsDownloading(true);

    try {
      await downloadPrivateFile(config.templatePath, config.templateFileName);
    } catch (caught) {
      toast.error(
        'Could not download the template',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDownloading(false);
    }
  }

  function downloadRejectedRows() {
    if (!result || result.rejected === 0) return;

    const rejectedRows = result.rows.filter((row) => row.status === 'REJECTED');
    const header = ['Row Number', `${capitalise(config.noun.one)} Name`, 'Status', 'Issue / Reason'];
    const rows = rejectedRows.map((row) => [
      row.row,
      row.name,
      'Needs fixing',
      row.reason ?? '',
    ]);

    downloadCsv(config.templateFileName.replace('template.xlsx', 'needs-fixing.csv'), [
      header,
      ...rows,
    ]);
  }

  async function upload(file: File, options: ImportSheetOptions = {}) {
    setIsUploading(true);
    setResult(null);
    onResultChange?.(null);

    try {
      const imported = await config.upload(file, options);

      // Nothing was written: the office checks the numbers first.
      if (imported.awaitingConfirmation) {
        setHeldSheet({ file, result: imported });
        return;
      }

      setHeldSheet(null);
      setResult(imported);
      onResultChange?.(imported);

      if (imported.rejected > 0) {
        setActiveTab('REJECTED');
      } else if (imported.admitted > 0) {
        setActiveTab('ADMITTED');
      } else {
        setActiveTab('all');
      }

      if (imported.admitted > 0) {
        toast.success(
          `${imported.admitted} ${imported.admitted === 1 ? config.noun.one : config.noun.many} ${config.done}`,
          describeOutcome(imported, config),
        );
        onImported?.(imported);
      } else if (imported.rejected === 0) {
        // Everybody in the sheet is already on the roll — the usual result of
        // uploading the same file twice, and not a failure.
        toast.success(
          'Nobody new in that sheet',
          `All ${imported.skipped} ${imported.skipped === 1 ? 'row was' : 'rows were'} ${config.alreadyThere}.`,
        );
        onImported?.(imported);
      } else {
        toast.error(`Nothing was ${config.done}`, 'Every row needs attention — see the list below.');
      }
    } catch (caught) {
      // The server's messages name the reason — a file that is not a workbook,
      // a sheet with no rows, too many rows — so they are shown verbatim.
      toast.error(
        'Could not import that file',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
      setResult(null);
      onResultChange?.(null);
    } finally {
      setIsUploading(false);
      // Cleared so choosing the same corrected file again still fires `change`.
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* --- 1. the template ------------------------------------------- */}
      <ol className="flex flex-col gap-3">
        <li className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-line bg-surface-2 p-3.5">
          <div className="min-w-0">
            <p className="text-sm font-[560] text-ink">1. Download the template</p>
            <p className="text-xs text-ink-2">
              It carries the exact columns this institute needs, an example row, and a Notes sheet
              explaining each one.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            isLoading={isDownloading}
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => void downloadTemplate()}
          >
            Download template
          </Button>
        </li>

        <li className="rounded-field border border-line bg-surface-2 p-3.5">
          <p className="text-sm font-[560] text-ink">2. Fill it in</p>
          <p className="text-xs text-ink-2">{config.fillInHint}</p>
        </li>

        {/* --- 3. upload ---------------------------------------------- */}
        <li className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-line bg-surface-2 p-3.5">
          <div className="min-w-0">
            <p className="text-sm font-[560] text-ink">3. Upload it</p>
            <p className="text-xs text-ink-2">{config.uploadHint}</p>
          </div>

          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <Button
            variant="primary"
            size="sm"
            isLoading={isUploading}
            leftIcon={<Upload className="h-4 w-4" />}
            onClick={() => fileInput.current?.click()}
          >
            Choose a file
          </Button>
        </li>
      </ol>

      {/* --- shared parent mobiles, waiting on the office -------------- */}
      {heldSheet && (
        <SharedMobilesCheck
          sharedMobiles={heldSheet.result.sharedMobiles ?? []}
          isImporting={isUploading}
          onConfirm={() => void upload(heldSheet.file, { confirmSharedMobiles: true })}
          onCancel={() => setHeldSheet(null)}
        />
      )}

      {/* --- what happened ------------------------------------------- */}
      {result && (
        <div className="flex flex-col gap-3.5 border-t border-line pt-4">
          {/* A sheet carries no files, so every child it admitted is still
              owed their certificates. Said here, at the moment it happened. */}
          {(result.documentsPending ?? 0) > 0 && (
            <Alert tone="warning" title="Documents still to attach">
              {result.documentsPending === 1
                ? 'The student admitted from this sheet has no certificates on file yet.'
                : `The ${result.documentsPending} students admitted from this sheet have no certificates on file yet.`}{' '}
              Open each student's record to attach them. On the Students list, use “Show only
              these students” to see everyone still waiting.
            </Alert>
          )}
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-800">
            <Tabs
              label="Import results"
              options={[
                {
                  value: 'ADMITTED' as const,
                  label: capitalise(config.done),
                  badge: result.admitted,
                },
                ...(result.rejected > 0
                  ? [
                      {
                        value: 'REJECTED' as const,
                        label: 'Need fixing',
                        badge: result.rejected,
                      },
                    ]
                  : []),
                ...(result.skipped > 0
                  ? [
                      {
                        value: 'SKIPPED' as const,
                        label: 'Skipped',
                        badge: result.skipped,
                      },
                    ]
                  : []),
                {
                  value: 'all' as const,
                  label: 'All rows',
                  badge: result.totalRows,
                },
              ]}
              value={activeTab}
              onChange={setActiveTab}
              className="border-b-0"
            />

            {activeTab === 'REJECTED' && result.rejected > 0 && (
              <button
                type="button"
                onClick={downloadRejectedRows}
                title="Download rows that need fixing"
                aria-label="Download rows that need fixing"
                className="flex items-center justify-center p-1.5 mb-1.5 rounded-lg text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 border border-slate-200 dark:border-slate-800 hover:border-rose-200 dark:hover:border-rose-900/60 transition-colors cursor-pointer shrink-0"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Who did not go in, named — before the row-by-row table, which is
              where four rejections hide on a sheet of two hundred. */}
          <ImportRejectionList result={result} statusFilter={activeTab} config={config} />

          <div className="max-h-80 overflow-y-auto rounded-card border border-line">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">What happened to each row of the spreadsheet</caption>
              <thead className="sticky top-0">
                <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
                  <th scope="col" className="w-16 px-4 py-3">
                    Row
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {capitalise(config.noun.one)}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {result.rows
                  .filter((row) => activeTab === 'all' || row.status === activeTab)
                  .map((row) => {
                    const style = STATUS[row.status];
                    const Icon = style.icon;

                    return (
                      <tr key={row.row} className={style.row}>
                        <td className="px-3 py-2.5 font-mono text-xs text-ink-3">{row.row}</td>
                        <td className="px-3 py-2.5">
                          <p className="truncate font-[560] text-ink">{row.name}</p>
                          {row.admissionNumber && (
                            <p className="truncate font-mono text-xs text-ink-3">
                              {row.admissionNumber}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={cn(
                              'flex items-center gap-1.5 text-xs font-[560]',
                              row.status === 'ADMITTED' && 'text-ok',
                              row.status === 'SKIPPED' && 'text-warn',
                              row.status === 'REJECTED' && 'text-crit',
                            )}
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            {statusLabel(row.status, config)}
                          </span>
                          {row.reason && <p className="mt-0.5 text-xs text-ink-2">{row.reason}</p>}
                          {row.account && (
                            <p
                              className={cn(
                                'mt-0.5 text-xs',
                                row.account === 'INVITED' ? 'text-ink-2' : 'text-warn',
                              )}
                            >
                              {STUDENT_ACCOUNT_LABEL[row.account]}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The import offered as a card that expands in place.
 *
 * Kept for the admission form, where it sits above a four-section form and has
 * to stay out of the way until somebody wants it. The register uses
 * `BulkImportSteps` in a dialog instead.
 */
export function BulkImportPanel({ onImported }: BulkImportPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-field bg-accent-soft text-accent"
            aria-hidden
          >
            <FileSpreadsheet className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base">Import from a spreadsheet</h2>
            <p className="mt-0.5 text-sm text-ink-2">
              Already have the list in Excel? Download the template, fill it in, and upload it
              instead of using the form below.
            </p>
          </div>
        </div>

        <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={() => setIsOpen((open) => !open)}>
          {isOpen ? 'Hide' : 'Import students'}
        </Button>
      </div>

      {isOpen && (
        <div className="border-t border-line pt-4">
          <BulkImportSteps onImported={onImported} />
        </div>
      )}
    </div>
  );
}
